import React, { useEffect, useState } from 'react';
import { storage } from '@uhg-abyss/web/tools/storage';
import { registerAnalytics } from '@src/tools/analytics';
import {
  useProviderPreferenceQuery,
  formatVariables,
} from '@sde/widgets-core/src/common/hooks/useProviderPreferenceQuery';
import { event } from '@uhg-abyss/web/tools/event';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useDashboard } from '@src/state/dashboard';
import { useProvider } from '@src/state/provider';
import { useAssignedAdminQuery } from '@sde/widgets-core/src/common/hooks/useAssignedAdminQuery';
import { useAutoEnrollmentQuery } from '@sde/widgets-core/src/common/hooks/useAutoEnrollmentQuery';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { AuthenticateUserWidget } from './AuthenticateUserWidget';
import { IdleUserWidget } from './IdleUserWidget';

const providerOrgKeys = [
  'id',
  'corpMpin',
  'uhcProviderId',
  'firstName',
  'lastName',
  'registeredOrg',
  'paa',
  'ia',
  'uniqueId',
];

const isNotNull = (providerId, uhcProviderId, corpMpin) =>
  providerId !== null && uhcProviderId !== null && corpMpin !== null;

export const App = ({ setDigitalSecurityError, setIsContentLoading }) => {
  const permissionsStorageKey = 'provider-permissions';
  const { user } = useUser.state();
  const { setProvider } = useProvider.actions();
  const { updateUser } = useUser.actions();
  const { updateProviderPreferenceError } = useDashboard.actions();
  const { setProviderPreferenceComplete } = useDashboard.actions();
  const [isEnrollmentRequested, setIsEnrollmentRequested] = useState(false);
  const [, getAssignedAdmin] = useAssignedAdminQuery({
    onCompleted: (response) => {
      updateUser({ adminInfo: response });
    },
    onError: (error) => {},
  });
  const [, getAutoEnrollment] = useAutoEnrollmentQuery({});

  useEffect(() => {
    const isSuperOrInternalUser = ['super-user', 'internal-user'].includes(
      user?.admin?.role?.toLowerCase()
    );
    if (
      !isEnrollmentRequested &&
      !isSuperOrInternalUser &&
      user?.uuid &&
      user?.email &&
      getFeature('TRACKIT_AUTO_ENROLLMENT')
    ) {
      setIsEnrollmentRequested(true);
      const filteredOrg =
        user?.providerOrg?.filter(({ corpMpin, id, uhcProviderId }) => {
          return isNotNull(id, uhcProviderId, corpMpin);
        }) || [];

      const excludedProviderOrgArray = filteredOrg.map((providerOrg) => {
        const excludedObject = {};
        Object.entries(providerOrg).forEach(([key, value]) => {
          if (providerOrgKeys.includes(key)) {
            excludedObject[key] = value;
          }
        });
        return excludedObject;
      });

      const limitToFiveProviderOrgs = excludedProviderOrgArray?.slice(0, 5);
      if (getFeature('TRACKIT_AUTO_ENROLLMENT_CHECK')) {
        getAutoEnrollment({
          variables: {
            uuid: user.uuid,
            emailAddress: user.email,
            providerOrg:
              excludedProviderOrgArray?.length > 0
                ? limitToFiveProviderOrgs
                : [],
          },
        });
      } else if (excludedProviderOrgArray?.length > 0) {
        getAutoEnrollment({
          variables: {
            uuid: user.uuid,
            emailAddress: user.email,
            providerOrg: limitToFiveProviderOrgs,
          },
        });
      }
    }
  }, [
    user?.uuid,
    user?.email,
    user?.admin?.role,
    user?.providerOrg,
    isEnrollmentRequested,
  ]);

  const [, getProviderPreference] = useProviderPreferenceQuery({
    onError: (error) => {
      const { code, message } = error;
      updateProviderPreferenceError({ message });
      setDigitalSecurityError({
        code,
        message,
        isDigitalSecurityError: true,
        isProviderPreferenceError: true,
      });
      setProviderPreferenceComplete(true);

      updateUser({
        code,
      });
      setIsContentLoading(false);
    },
    onCompleted: (response) => {
      if (
        response?.permissions?.length > 0 ||
        response?.permissionSubCode?.length > 0 ||
        response?.permissionCode?.length > 0 ||
        response?.allPermission
      ) {
        setProvider({
          ...response.provider,
          permissions: response.permissions || [],
          privileges: response.organizations || [],
          allPermission: response.allPermission || false,
          permissionSubCode: response.permissionSubCode || [],
          permissionCode: response.permissionCode || [],
        });

        updateUser({
          provider: response.provider,
          permissions: response.permissions || [],
          privileges: response.organizations || [],
          allPermission: response.allPermission || false,
          permissionSubCode: response.permissionSubCode || [],
          permissionCode: response.permissionCode || [],
        });
      }

      getAssignedAdmin({
        variables: {
          id: response.provider.userId,
          allStatus: true,
        },
      });
      storage.local.set(permissionsStorageKey, response.permissions);
      const providerData = {
        corporateProviderName:
          response.corporateProviderName ||
          response.provider?.corporateProviderName,
        taxIdNumber: response.taxIdNumber || response.provider?.taxIdNumber,
        corporateProviderMpin:
          response.corporateProviderMpin ||
          response.provider?.corporateProviderMpin,
        careProviderMpin:
          response.servicingProviderMpin ||
          response.provider?.servicingProviderMpin,
        careProviderName:
          response.servicingProviderName ||
          response.provider?.servicingProviderName,
        careProviderFirstName:
          response.servicingProviderFirstName ||
          response.provider?.servicingProviderFirstName,
        careProviderLastName:
          response.servicingProviderLastName ||
          response.provider?.servicingProviderLastName,
        allPermission: response.allPermission || false,
        permissionCode: response.permissionCode || [],
        tpsm: response.provider?.tpsm || [],
      };

      event.setData({ ...providerData });
      setProviderPreferenceComplete(true);
      setIsContentLoading(false);
    },
  });

  useEffect(() => {
    registerAnalytics();
  }, []);

  useEffect(() => {
    if (user.uuid) {
      getProviderPreference({
        variables: formatVariables(user),
      });
    }
  }, [user.uuid]);

  return (
    <>
      <IdleUserWidget />
      <AuthenticateUserWidget />
    </>
  );
};
